import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { ColumnDef } from '@tanstack/react-table'
import { DownloadSearchResultButton } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsSearchActions/DownloadSearchResultButton'
import { MiddleTruncatedText } from 'features/knowledgeDocumentViewer/components/MiddleTruncatedText'
import { documentTypeLabel } from 'features/knowledgeDocumentViewer/utils/documentTypeLabels'
import { compareRevisionCodes } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import {
  formatDocumentDisplayTitle,
  toTitleCase,
} from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { InputType } from 'types'

export type DocumentTableRow = {
  cmmsVersion?: string
  cmms?: {
    number: string
    status: string
    assetNumber?: string | null
    location?: string | null
    updatedAt?: string | null
  }
  /** Stable BigQuery document_id used in URLs and tab navigation. */
  id: string
  /** Rolodex system-document entry id used for OCR/knowledge tag queries. */
  systemEntryId: string
  title: string
  filename: string
  documentTitle: string | null
  system: string | null
  vendor: string | null
  docType: string | null
  fileType: string | null
  drawing: string | null
  sheetNumber?: string | null
  timestamp: string | null
  numPages: number | null
  revisionCode: string | null
  revisionDate: string | null
  summary: string | null
  keywords: string[]
  /** Bare BigQuery document_id for UUID5 cantilever host derive. */
  bqDocumentId?: string
  /** Rolodex cantilever-document entry id when tagged on the hydrated system document. */
  cantileverDocumentEntryId?: string
}

export const DocumentTableColumns = (
  translate: (descriptor: MessageDescriptor) => string
): ColumnDef<DocumentTableRow>[] => [
  {
    id: 'title',
    accessorKey: 'title',
    size: 230,
    header: translate(msg`File Name`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 140,
    cell: ({ row }) => {
      const titleCasedTitle = toTitleCase(row.original.title)
      return (
        <MiddleTruncatedText
          text={titleCasedTitle}
          maxWidth='100%'
          fillWidth
          tooltipFontSize='0.75rem'
        />
      )
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'documentTitle',
    accessorKey: 'documentTitle',
    size: 320,
    header: translate(msg`Document Title`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 140,
    cell: ({ row }) => {
      const value = row.original.documentTitle
        ? formatDocumentDisplayTitle(
            row.original.documentTitle,
            row.original.title
          )
        : '-'
      return (
        <MiddleTruncatedText
          text={value}
          maxWidth='100%'
          fillWidth
          tooltipFontSize='0.75rem'
        />
      )
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'numPages',
    accessorKey: 'numPages',
    size: 104,
    header: translate(msg`Length`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <span>{row.original.numPages ?? 1}</span>
    },
  },
  {
    id: 'system',
    accessorKey: 'system',
    size: 180,
    header: translate(msg`System`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 110,
    cell: ({ row }) => {
      return <span>{row.original.system || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'vendor',
    accessorKey: 'vendor',
    size: 200,
    header: translate(msg`Vendor`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'multiSelectFilter',
    minSize: 110,
    cell: ({ row }) => {
      return <span>{row.original.vendor || '-'}</span>
    },
    meta: {
      inputType: InputType.MultiSelect,
    },
  },
  {
    id: 'docType',
    accessorKey: 'docType',
    header: translate(msg`Type`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'multiSelectFilter',
    minSize: 120,
    size: 150,
    cell: ({ row }) => {
      return (
        <span>
          {row.original.docType
            ? documentTypeLabel(row.original.docType, translate)
            : '-'}
        </span>
      )
    },
    meta: {
      inputType: InputType.MultiSelect,
    },
  },
  {
    id: 'drawing',
    accessorKey: 'drawing',
    size: 160,
    header: translate(msg`Drawing Number`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 120,
    cell: ({ row }) => {
      return <span>{row.original.drawing || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'revisionCode',
    accessorKey: 'revisionCode',
    size: 104,
    header: translate(msg`Rev`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    sortingFn: (rowA, rowB) => {
      const revA = rowA.original.revisionCode ?? ''
      const revB = rowB.original.revisionCode ?? ''
      return compareRevisionCodes(revA, revB)
    },
    cell: ({ row }) => {
      return <span>{row.original.revisionCode || '-'}</span>
    },
  },
  {
    id: 'revisionDate',
    accessorKey: 'revisionDate',
    size: 110,
    header: translate(msg`Date`),
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <span>{row.original.revisionDate || '-'}</span>
    },
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    enableColumnFilter: false,
    minSize: 48,
    size: 48,
    cell: ({ row }) => (
      <DownloadSearchResultButton document={row.original} size='icon' />
    ),
  },
]
